/**
 * Carrier-Kontrolle — SOLL/IST-Vergleich der Carrier-Rechnungen (Post, DPD, GLS etc.).
 *
 * Features:
 * - Monatsvergleich: SOLL (aus Fulfillment) vs. IST (Carrier-Rechnungen)
 * - Korrekturbuchungen (Adjustments) pro Carrier und Monat
 * - Carrier-Mapping, nicht zugeordnete Rechnungen, Trend-Analyse
 * - Rechnungs-Neuzuordnung (Reassign) und Partner-Zuordnung
 *
 * @page /carrier-kontrolle
 * @roles admin, manager
 * @api /api/carrier-kontrolle/vergleich, /api/carrier-kontrolle/detail
 * @api /api/carrier-kontrolle/trend, /api/carrier-kontrolle/adjustments
 * @api /api/carrier-kontrolle/mapping, /api/carrier-kontrolle/invoices
 * @api /api/carrier-kontrolle/unassigned-invoices, /api/carrier-kontrolle/reassign
 * @api /api/carrier-kontrolle/assign-partner, /api/carrier-kontrolle/remove-assignments
 * @i18n carrier
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import { GRID, AXIS_PROPS, TOOLTIP_STYLE, LEGEND_STYLE, ANIMATION, fmtCompact, fmtEur as fmtEurShared, withOpacity } from '../utils/chartConfig';
import { PageHeader, Badge, Button, KpiCard } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

const MOD = '#338edc';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  return fetch(path, {
    ...opts,
    headers: hasBody ? { 'Content-Type': 'application/json', ...opts.headers } : opts.headers,
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(r => r.json());
}

function fmtEur(n) {
  if (n == null || isNaN(n)) return '0,00 €';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtNum(n) {
  if (n == null) return '0';
  return Math.round(n).toLocaleString('de-DE');
}
function fmtPct(n) {
  if (n == null || isNaN(n)) return '0,0%';
  return (n >= 0 ? '+' : '') + n.toFixed(1).replace('.', ',') + '%';
}
function deltaColor(v) { return v > 0 ? 'text-red-400' : v < 0 ? 'text-emerald-400' : 'text-[var(--text-secondary)]'; }
function deltaBg(v, threshold = 10) {
  if (Math.abs(v) >= threshold) return v > 0 ? 'bg-red-500/10' : 'bg-emerald-500/10';
  return '';
}

// ─── Adjustment Popover ──────────────────────────────────────────

function AdjustmentCell({ carrier, carrierName, jahr, monat, adjSoll, adjIst, onSaved, inline }) {
  const { t } = useLanguage();
  const monthNames = Array.from({length: 12}, (_, i) => t('common.months.' + i));
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editBetrag, setEditBetrag] = useState('');
  const [editBezeichnung, setEditBezeichnung] = useState('');
  const [editTyp, setEditTyp] = useState('soll_korrektur');
  const [showNew, setShowNew] = useState(false);
  const [newBetrag, setNewBetrag] = useState('');
  const [newBezeichnung, setNewBezeichnung] = useState('');
  const [newTyp, setNewTyp] = useState('soll_korrektur');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const ref = useRef(null);
  const totalAdj = (adjSoll || 0) + (adjIst || 0);

  const reload = async () => {
    const rows = await api(`/api/carrier-kontrolle/adjustments?carrier=${carrier}&jahr=${jahr}&monat=${monat}`);
    if (Array.isArray(rows)) setItems(rows);
  };

  useEffect(() => { if (open) reload(); }, [open, carrier, jahr, monat]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const summe = items.reduce((s, it) => s + parseFloat(it.betrag || 0), 0);

  const startEdit = (it) => {
    setEditId(it.id);
    setEditBetrag(String(parseFloat(it.betrag)));
    setEditBezeichnung(it.bezeichnung || '');
    setEditTyp(it.typ || 'soll_korrektur');
    setDeleteConfirm(null);
  };
  const cancelEdit = () => { setEditId(null); };

  const handleUpdate = async () => {
    if (!editBetrag || !editBezeichnung.trim()) return;
    setSaving(true);
    await api(`/api/carrier-kontrolle/adjustments/${editId}`, {
      method: 'PUT',
      body: { betrag: parseFloat(editBetrag), bezeichnung: editBezeichnung.trim(), typ: editTyp },
    });
    setEditId(null);
    setSaving(false);
    await reload();
    onSaved();
  };

  const handleDelete = async (id) => {
    setSaving(true);
    await api(`/api/carrier-kontrolle/adjustments/${id}`, { method: 'DELETE' });
    setDeleteConfirm(null);
    setSaving(false);
    await reload();
    onSaved();
  };

  const handleCreate = async () => {
    if (!newBetrag || !newBezeichnung.trim()) return;
    setSaving(true);
    await api('/api/carrier-kontrolle/adjustments', {
      method: 'POST',
      body: { carrier_key: carrier, jahr, monat, betrag: parseFloat(newBetrag), bezeichnung: newBezeichnung.trim(), typ: newTyp },
    });
    setNewBetrag(''); setNewBezeichnung(''); setNewTyp('soll_korrektur'); setShowNew(false);
    setSaving(false);
    await reload();
    onSaved();
  };

  const inputCls = 'bg-surface-inset border border-line-hover rounded px-2 py-1 text-xs text-content font-mono focus:outline-none focus:border-primary-500';

  const trigger = (
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); setEditId(null); setShowNew(false); setDeleteConfirm(null); }}
        className={inline
          ? `ml-1 shrink-0 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
              totalAdj !== 0
                ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 font-semibold'
                : 'text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100 hover:bg-surface-hover hover:text-[var(--text-secondary)]'
            }`
          : `font-mono text-xs px-2 py-1 rounded transition-colors ${
              totalAdj !== 0
                ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 font-semibold'
                : 'text-[var(--text-tertiary)] hover:bg-surface-hover hover:text-[var(--text-secondary)]'
            }`}
        title={totalAdj !== 0 ? `${t('carrier.sollCorrection')}: ${fmtEur(adjSoll)}, ${t('carrier.istCorrection')}: ${fmtEur(adjIst)}` : t('carrier.addCorrection')}
      >
        {inline ? (totalAdj !== 0 ? `±${Math.abs(totalAdj).toFixed(0)}` : '±') : (totalAdj !== 0 ? fmtEur(totalAdj) : '—')}
      </button>
  );

  return (
    <span className="relative" onClick={e => e.stopPropagation()}>
      {trigger}
      {open && (
        <div ref={ref} className="absolute left-0 top-full mt-1 z-40 w-[400px] bg-surface-elevated border border-line-hover rounded-card shadow-2xl text-left">
          {/* Header */}
          <div className="px-4 pt-3 pb-2 border-b border-line">
            <div className="text-xs font-semibold text-content">
              {t('carrier.corrections')}: {carrierName} — {monthNames[monat - 1]} {jahr}
            </div>
          </div>

          {/* Items list */}
          <div className="px-4 py-2 space-y-1 max-h-64 overflow-y-auto">
            {items.length === 0 && !showNew && (
              <div className="text-[11px] text-[var(--text-tertiary)] py-3 text-center">{t('carrier.noCorrections')}</div>
            )}
            {items.map(it => (
              <div key={it.id}>
                {editId === it.id ? (
                  /* ── Inline Edit Mode ── */
                  <div className="bg-surface-card border border-primary-500/40 rounded-card p-2.5 space-y-2">
                    <input value={editBezeichnung} onChange={e => setEditBezeichnung(e.target.value)}
                      className={`${inputCls} w-full !font-sans`} placeholder={t('carrier.description')} />
                    <div className="flex gap-2">
                      <input type="number" step="0.01" value={editBetrag} onChange={e => setEditBetrag(e.target.value)}
                        className={`${inputCls} flex-1`} placeholder={t('carrier.amount')} />
                      <select value={editTyp} onChange={e => setEditTyp(e.target.value)}
                        className={`${inputCls} !font-sans`}>
                        <option value="soll_korrektur">SOLL</option>
                        <option value="ist_korrektur">IST</option>
                      </select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={cancelEdit}
                        className="px-2.5 py-1 rounded text-[11px] text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">
                        {t('common.cancel')}
                      </button>
                      <button onClick={handleUpdate} disabled={saving || !editBetrag || !editBezeichnung.trim()}
                        className="px-2.5 py-1 rounded text-[11px] font-semibold bg-[#338edc] text-white hover:bg-[#2569A8] transition-colors disabled:opacity-40">
                        {saving ? '...' : t('common.save')}
                      </button>
                    </div>
                  </div>
                ) : deleteConfirm === it.id ? (
                  /* ── Delete Confirmation ── */
                  <div className="bg-red-500/10 border border-red-500/30 rounded-card p-2.5">
                    <div className="text-[11px] text-content mb-2">
                      {t('carrier.deleteConfirmCorrection', { name: it.bezeichnung })}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setDeleteConfirm(null)}
                        className="px-2.5 py-1 rounded text-[11px] text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">
                        {t('common.cancel')}
                      </button>
                      <button onClick={() => handleDelete(it.id)} disabled={saving}
                        className="px-2.5 py-1 rounded text-[11px] font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-40">
                        {saving ? '...' : t('common.delete')}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Display Row ── */
                  <div className="flex items-center gap-2 text-[11px] bg-surface-card border border-line rounded-card px-3 py-2 group">
                    <span className="flex-1 text-content truncate">{it.bezeichnung || '—'}</span>
                    <span className={`font-mono font-semibold whitespace-nowrap ${parseFloat(it.betrag) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {parseFloat(it.betrag) > 0 ? '+' : ''}{fmtEur(parseFloat(it.betrag))}
                    </span>
                    <Badge variant={it.typ === 'soll_korrektur' ? 'warning' : 'info'} className="text-[9px] px-1 py-0.5">{it.typ === 'soll_korrektur' ? 'SOLL' : 'IST'}</Badge>
                    <button onClick={() => startEdit(it)}
                      className="opacity-0 group-hover:opacity-100 text-[var(--text-tertiary)] hover:text-primary-400 transition-all shrink-0" title={t('common.edit')}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button onClick={() => { setDeleteConfirm(it.id); setEditId(null); }}
                      className="opacity-0 group-hover:opacity-100 text-[var(--text-tertiary)] hover:text-red-400 transition-all shrink-0" title={t('common.delete')}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* New Korrektur inline form */}
            {showNew && (
              <div className="bg-surface-card border border-emerald-500/40 rounded-card p-2.5 space-y-2">
                <input value={newBezeichnung} onChange={e => setNewBezeichnung(e.target.value)}
                  className={`${inputCls} w-full !font-sans`} placeholder={t('carrier.descriptionPlaceholder')} autoFocus />
                <div className="flex gap-2">
                  <input type="number" step="0.01" value={newBetrag} onChange={e => setNewBetrag(e.target.value)}
                    className={`${inputCls} flex-1`} placeholder={t('carrier.amountPlaceholder')} />
                  <select value={newTyp} onChange={e => setNewTyp(e.target.value)}
                    className={`${inputCls} !font-sans`}>
                    <option value="soll_korrektur">SOLL</option>
                    <option value="ist_korrektur">IST</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setShowNew(false); setNewBetrag(''); setNewBezeichnung(''); }}
                    className="px-2.5 py-1 rounded text-[11px] text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">
                    {t('common.cancel')}
                  </button>
                  <button onClick={handleCreate} disabled={saving || !newBetrag || !newBezeichnung.trim()}
                    className="px-2.5 py-1 rounded text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-40">
                    {saving ? '...' : t('carrier.add')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Summe + Add button */}
          <div className="px-4 py-2.5 border-t border-line">
            {items.length > 0 && (
              <div className="flex items-center justify-between text-[11px] mb-2">
                <span className="text-content-tertiary font-medium">{t('carrier.sum')}:</span>
                <span className="font-mono font-bold text-content">
                  {summe > 0 ? '+' : ''}{fmtEur(summe)}
                </span>
              </div>
            )}
            {!showNew && (
              <button onClick={() => { setShowNew(true); setEditId(null); setDeleteConfirm(null); }}
                className="w-full py-1.5 rounded-lg text-[11px] font-medium text-content-tertiary border border-dashed border-line-hover hover:border-content-dim hover:text-content hover:bg-surface-hover transition-colors">
                + {t('carrier.addNewCorrection')}
              </button>
            )}
          </div>
        </div>
      )}
    </span>
  );
}

// ─── Detail Modal ───────────────────────────────────────────────

function DetailModal({ carrier, jahr, monat, onClose }) {
  const { t } = useLanguage();
  const monthNames = Array.from({length: 12}, (_, i) => t('common.months.' + i));
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const bisY = jahr, bisM = monat;
    let vonY = jahr - 1, vonM = monat + 1;
    if (vonM > 12) { vonM = 1; vonY++; }
    const von = `${vonY}-${String(vonM).padStart(2, '0')}`;
    const bis = `${bisY}-${String(bisM).padStart(2, '0')}`;

    Promise.all([
      api(`/api/carrier-kontrolle/detail?carrier=${carrier}&jahr=${jahr}&monat=${monat}`),
      api(`/api/carrier-kontrolle/trend?carrier=${carrier}&von=${von}&bis=${bis}`),
    ]).then(([d, t]) => {
      setData(d);
      setTrend(t);
      setLoading(false);
    });
  }, [carrier, jahr, monat]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-elevated border border-line-hover rounded-card w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="text-lg font-bold">{data?.carrier?.display_name || carrier} — {monthNames[(monat || 1) - 1]} {jahr}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-6">
          {loading ? (
            <div className="text-center text-[var(--text-tertiary)] py-12">{t('carrier.loadingData')}</div>
          ) : (
            <>
              {trend?.months?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-content-tertiary uppercase tracking-wider mb-3">{t('carrier.sollIstTrend12m')}</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trend.months} {...ANIMATION}>
                        <defs>
                          <linearGradient id="detailIstGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={data?.carrier?.color || MOD} stopOpacity={0.15} />
                            <stop offset="100%" stopColor={data?.carrier?.color || MOD} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid {...GRID} />
                        <XAxis dataKey="monat" {...AXIS_PROPS} tickFormatter={v => { const [,mm] = v.split('-'); return monthNames[parseInt(mm)-1]?.substring(0,3); }} />
                        <YAxis {...AXIS_PROPS} tickFormatter={fmtCompact} />
                        <Tooltip {...TOOLTIP_STYLE} formatter={(v) => fmtEur(v)} labelFormatter={v => { const [yy,mm] = v.split('-'); return monthNames[parseInt(mm)-1] + ' ' + yy; }} />
                        <Area type="monotone" dataKey="ist" name={t('carrier.istLabel')} stroke={data?.carrier?.color || MOD} strokeWidth={2.5} fill="url(#detailIstGrad)" dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                        <Line type="monotone" dataKey="soll" name={t('carrier.sollLabel')} stroke={withOpacity(data?.carrier?.color || MOD, 0.5)} strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                        <Legend {...LEGEND_STYLE} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {data?.soll_products?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-content-tertiary uppercase tracking-wider mb-3">{t('carrier.sollBreakdownTopProducts')}</h3>
                  <div className="bg-surface-card border border-line rounded-card overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-line text-content-tertiary">
                        <th className="text-left px-3 py-2">{t('carrier.product')}</th>
                        <th className="text-right px-3 py-2">{t('carrier.packages')}</th>
                        <th className="text-right px-3 py-2">{t('carrier.costEk')}</th>
                        <th className="text-right px-3 py-2">{t('carrier.priceVk')}</th>
                      </tr></thead>
                      <tbody>
                        {data.soll_products.slice(0, 15).map((p, i) => (
                          <tr key={i} className="border-b border-line/50 last:border-0">
                            <td className="px-3 py-2 text-content truncate max-w-xs">{p.name}</td>
                            <td className="px-3 py-2 text-right text-[var(--text-secondary)] font-mono">{fmtNum(p.qty)}</td>
                            <td className="px-3 py-2 text-right text-content font-mono">{fmtEur(p.cost)}</td>
                            <td className="px-3 py-2 text-right text-[var(--text-secondary)] font-mono">{fmtEur(p.price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-content-tertiary uppercase tracking-wider mb-3">
                  {t('carrier.istInvoices')} ({data?.ist_invoices?.length || 0})
                </h3>
                {data?.ist_invoices?.length > 0 ? (
                  <div className="bg-surface-card border border-line rounded-card overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-line text-content-tertiary">
                        <th className="text-left px-3 py-2">{t('carrier.invoice')}</th>
                        <th className="text-left px-3 py-2">{t('carrier.reference')}</th>
                        <th className="text-left px-3 py-2">{t('carrier.partner')}</th>
                        <th className="text-left px-3 py-2">{t('carrier.date')}</th>
                        <th className="text-right px-3 py-2">{t('carrier.net')}</th>
                        <th className="text-center px-3 py-2">{t('common.status')}</th>
                      </tr></thead>
                      <tbody>
                        {data.ist_invoices.map((inv, i) => (
                          <tr key={i} className="border-b border-line/50 last:border-0">
                            <td className="px-3 py-2 text-content font-mono">{inv.name}</td>
                            <td className="px-3 py-2 text-[var(--text-secondary)]">{inv.ref || '—'}</td>
                            <td className="px-3 py-2 text-[var(--text-secondary)] truncate max-w-[120px]">{inv.partner}</td>
                            <td className="px-3 py-2 text-[var(--text-secondary)]">{inv.date}</td>
                            <td className="px-3 py-2 text-right text-content font-mono">{fmtEur(inv.amount_netto)}</td>
                            <td className="px-3 py-2 text-center">
                              <Badge variant={inv.payment_state === 'paid' ? 'success' : inv.payment_state === 'partial' ? 'warning' : 'error'} className="text-[10px]">
                                {inv.payment_state === 'paid' ? t('carrier.paid') : inv.payment_state === 'partial' ? t('carrier.partial') : t('carrier.open')}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-[var(--text-tertiary)] text-sm py-4 text-center">{t('carrier.noInvoicesForCarrier')}</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Mapping Admin Modal ────────────────────────────────────────

function MappingModal({ onClose, onSaved }) {
  const { t } = useLanguage();
  const [mappings, setMappings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newPartners, setNewPartners] = useState('');
  const [newColor, setNewColor] = useState('#6b7280');
  const [newError, setNewError] = useState('');

  const loadMappings = () => {
    api('/api/carrier-kontrolle/mapping').then(rows => {
      if (Array.isArray(rows)) setMappings(rows.map(r => ({ ...r, partner_str: (r.odoo_partner_ids || []).join(', ') })));
    });
  };

  useEffect(() => { loadMappings(); }, []);

  const update = (i, field, val) => {
    setMappings(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: val } : m));
  };

  const handleSave = async () => {
    setSaving(true);
    const arr = mappings.map(m => ({
      carrier_key: m.carrier_key, display_name: m.display_name,
      odoo_partner_ids: (m.partner_str || '').split(',').map(s => parseInt(s.trim())).filter(n => n > 0),
      color: m.color, active: m.active,
    }));
    await api('/api/carrier-kontrolle/mapping', { method: 'PUT', body: { mappings: arr } });
    setSaving(false);
    onSaved();
    onClose();
  };

  const handleCreateCarrier = async () => {
    const key = newKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!key || !newName.trim()) { setNewError(t('carrier.keyNameRequired')); return; }
    if (mappings.some(m => m.carrier_key === key)) { setNewError(t('carrier.keyAlreadyExists')); return; }
    setSaving(true);
    setNewError('');
    const res = await api('/api/carrier-kontrolle/mapping', {
      method: 'POST',
      body: {
        carrier_key: key, display_name: newName.trim(),
        odoo_partner_ids: newPartners ? newPartners.split(',').map(s => parseInt(s.trim())).filter(n => n > 0) : [],
        color: newColor,
      },
    });
    setSaving(false);
    if (res.error) { setNewError(res.error); return; }
    setShowNew(false); setNewKey(''); setNewName(''); setNewPartners(''); setNewColor('#6b7280');
    loadMappings();
  };

  const inputCls = 'bg-surface-inset border border-line-hover rounded px-2 py-1 text-xs text-content focus:outline-none focus:border-primary-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-elevated border border-line-hover rounded-card w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="text-lg font-bold">{t('carrier.editMapping')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-3">
          {mappings.map((m, i) => (
            <div key={m.carrier_key} className="bg-surface-card border border-line rounded-card p-3 flex items-center gap-3">
              <input type="color" value={m.color || '#6b7280'} onChange={e => update(i, 'color', e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" />
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--text-tertiary)] font-mono w-20">{m.carrier_key}</span>
                  <input value={m.display_name} onChange={e => update(i, 'display_name', e.target.value)}
                    className={`flex-1 ${inputCls}`} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--text-tertiary)] w-20">{t('carrier.partnerIds')}</span>
                  <input value={m.partner_str} onChange={e => update(i, 'partner_str', e.target.value)}
                    placeholder={t('carrier.partnerIdsPlaceholder')}
                    className={`flex-1 ${inputCls} font-mono`} />
                </div>
              </div>
              <label className="flex items-center gap-1 text-[10px] text-[var(--text-secondary)] cursor-pointer">
                <input type="checkbox" checked={m.active} onChange={e => update(i, 'active', e.target.checked)} className="accent-primary-500" />
                {t('common.active')}
              </label>
            </div>
          ))}

          {/* New Carrier Form */}
          {showNew ? (
            <div className="bg-surface-card border border-emerald-500/40 rounded-card p-4 space-y-2.5">
              <div className="text-xs font-semibold text-content">{t('carrier.newCarrier')}</div>
              <div className="flex items-center gap-2">
                <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" />
                <div className="flex-1 space-y-1.5">
                  <div className="flex gap-2">
                    <input value={newKey} onChange={e => setNewKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                      placeholder={t('carrier.carrierKeyPlaceholder')}
                      className={`w-32 ${inputCls} font-mono`} />
                    <input value={newName} onChange={e => setNewName(e.target.value)}
                      placeholder={t('carrier.displayNamePlaceholder')}
                      className={`flex-1 ${inputCls}`} />
                  </div>
                  <input value={newPartners} onChange={e => setNewPartners(e.target.value)}
                    placeholder={t('carrier.odooPartnerIdsPlaceholder')}
                    className={`w-full ${inputCls} font-mono`} />
                </div>
              </div>
              {newError && <div className="text-xs text-red-400">{newError}</div>}
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowNew(false); setNewError(''); }}
                  className="px-3 py-1.5 rounded-lg text-xs text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">
                  {t('common.cancel')}
                </button>
                <button onClick={handleCreateCarrier} disabled={saving || !newKey.trim() || !newName.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-40">
                  {saving ? '...' : t('carrier.create')}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowNew(true)}
              className="w-full py-2 rounded-card text-xs font-medium text-content-tertiary border border-dashed border-line-hover hover:border-content-dim hover:text-content hover:bg-surface-hover transition-colors">
              + {t('carrier.newCarrier')}
            </button>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-line">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-inset transition-colors">{t('common.cancel')}</button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-surface-active text-content hover:bg-surface-active/80 transition-colors disabled:opacity-50">
              {saving ? `${t('common.save')}...` : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Unassigned Invoices Modal ──────────────────────────────────

function UnassignedModal({ jahr, monat, onClose, onAssigned }) {
  const { t } = useLanguage();
  const monthNames = Array.from({length: 12}, (_, i) => t('common.months.' + i));
  const [invoices, setInvoices] = useState([]);
  const [carrierList, setCarrierList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState({});

  const loadInvoices = useCallback(() => {
    setLoading(true);
    api(`/api/carrier-kontrolle/unassigned-invoices?jahr=${jahr}&monat=${monat}`)
      .then(res => {
        if (res.invoices) setInvoices(res.invoices);
        if (res.carriers) setCarrierList(res.carriers);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [jahr, monat]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices;
    const s = search.toLowerCase();
    return invoices.filter(r =>
      (r.name || '').toLowerCase().includes(s) ||
      (r.partner_name || '').toLowerCase().includes(s) ||
      (r.ref || '').toLowerCase().includes(s) ||
      String(r.amount_netto).includes(s)
    );
  }, [invoices, search]);

  const handleAssign = async (inv, carrierKey) => {
    if (!carrierKey) return;
    setAssigning(prev => ({ ...prev, [inv.id]: true }));
    const res = await api('/api/carrier-kontrolle/assign-partner', {
      method: 'POST',
      body: { carrier_key: carrierKey, partner_id: inv.partner_id },
    });
    setAssigning(prev => ({ ...prev, [inv.id]: false }));
    if (res.ok) {
      setInvoices(prev => prev.filter(i => i.partner_id !== inv.partner_id));
      onAssigned();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-elevated border border-line-hover rounded-card w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <div>
            <h2 className="text-lg font-bold">{t('carrier.unassignedInvoices')}</h2>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">{monthNames[monat - 1]} {jahr} — {t('carrier.vendorInvoicesWithoutCarrier')}</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none">&times;</button>
        </div>

        <div className="p-4 border-b border-line">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('carrier.searchInvoicePlaceholder')}
            className="w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-primary-500" />
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center text-[var(--text-tertiary)] py-12">{t('carrier.loadingInvoicesFromOdoo')}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-[var(--text-tertiary)] py-12">
              {invoices.length === 0 ? t('carrier.allInvoicesAssigned') : t('carrier.noSearchResults')}
            </div>
          ) : (
            <div className="bg-surface-card border border-line rounded-card overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-line text-content-tertiary">
                    <th className="text-left px-3 py-2">{t('carrier.invoiceNo')}</th>
                    <th className="text-left px-3 py-2">{t('carrier.vendor')}</th>
                    <th className="text-left px-3 py-2">{t('carrier.reference')}</th>
                    <th className="text-left px-3 py-2">{t('carrier.date')}</th>
                    <th className="text-right px-3 py-2">{t('carrier.net')}</th>
                    <th className="text-center px-3 py-2 w-44">{t('carrier.assign')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.id} className="border-b border-line/50 last:border-0">
                      <td className="px-3 py-2 text-content font-mono">{inv.name}</td>
                      <td className="px-3 py-2 text-content">{inv.partner_name}</td>
                      <td className="px-3 py-2 text-[var(--text-secondary)] truncate max-w-[120px]">{inv.ref || '—'}</td>
                      <td className="px-3 py-2 text-[var(--text-secondary)]">{inv.date}</td>
                      <td className="px-3 py-2 text-right text-content font-mono font-semibold">{fmtEur(inv.amount_netto)}</td>
                      <td className="px-3 py-2 text-center">
                        {assigning[inv.id] ? (
                          <span className="text-[var(--text-tertiary)] text-[10px]">{t('carrier.assigning')}</span>
                        ) : (
                          <select
                            defaultValue=""
                            onChange={e => handleAssign(inv, e.target.value)}
                            className="bg-surface-inset border border-line-hover rounded px-2 py-1 text-[11px] text-content focus:outline-none w-full"
                          >
                            <option value="" disabled>{t('carrier.selectCarrier')}</option>
                            {carrierList.map(c => (
                              <option key={c.carrier_key} value={c.carrier_key}>{c.display_name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="text-xs text-[var(--text-tertiary)] mt-3 text-center">
              {filtered.length} {filtered.length !== 1 ? t('carrier.invoicesPlural') : t('carrier.invoiceSingular')} {t('carrier.found')}
              {filtered.length !== invoices.length && ` (${invoices.length} ${t('common.total').toLowerCase()})`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Invoice Management Modal ────────────────────────────────

function InvoiceManagementModal({ jahr, monat, onClose, onChanged }) {
  const { t } = useLanguage();
  const monthNames = Array.from({length: 12}, (_, i) => t('common.months.' + i));
  const [invoices, setInvoices] = useState([]);
  const [carrierList, setCarrierList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [manualOnly, setManualOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [reassigning, setReassigning] = useState({});
  const [bulkCarrier, setBulkCarrier] = useState('');
  const [bulkAction, setBulkAction] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadInvoices = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ jahr, monat });
    if (carrierFilter) params.set('carrier', carrierFilter);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (manualOnly) params.set('manual_only', 'true');
    api(`/api/carrier-kontrolle/invoices?${params}`)
      .then(res => {
        if (res.invoices) setInvoices(res.invoices);
        if (res.carriers) setCarrierList(res.carriers);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [jahr, monat, carrierFilter, debouncedSearch, manualOnly]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const toggleSelectAll = () => {
    if (selected.size === invoices.length && invoices.length > 0) setSelected(new Set());
    else setSelected(new Set(invoices.map(i => i.account_move_id)));
  };
  const toggleSelect = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleReassign = async (moveId, newKey) => {
    if (!newKey) return;
    setReassigning(prev => ({ ...prev, [moveId]: true }));
    const res = await api('/api/carrier-kontrolle/reassign', {
      method: 'PUT', body: { account_move_ids: [moveId], new_carrier_key: newKey },
    });
    setReassigning(prev => ({ ...prev, [moveId]: false }));
    if (res.ok) { showToast(t('carrier.assignmentChanged')); loadInvoices(); onChanged(); }
    else showToast(res.error || t('common.error'), 'error');
  };

  const handleRemoveOverride = async (moveId) => {
    const res = await api('/api/carrier-kontrolle/remove-assignments', {
      method: 'POST', body: { account_move_ids: [moveId] },
    });
    setRemoveConfirm(null);
    if (res.ok) { showToast(t('carrier.manualAssignmentRemoved')); loadInvoices(); onChanged(); }
  };

  const handleBulkReassign = async () => {
    if (!bulkCarrier || selected.size === 0) return;
    setBulkAction(true);
    const res = await api('/api/carrier-kontrolle/reassign', {
      method: 'PUT', body: { account_move_ids: [...selected], new_carrier_key: bulkCarrier },
    });
    setBulkAction(false);
    if (res.ok) {
      const cName = carrierList.find(c => c.carrier_key === bulkCarrier)?.display_name || bulkCarrier;
      showToast(`${res.count} ${t('carrier.invoicesMovedTo')} ${cName}`);
      setSelected(new Set()); setBulkCarrier(''); loadInvoices(); onChanged();
    }
  };

  const handleBulkRemove = async () => {
    if (selected.size === 0) return;
    const manualIds = [...selected].filter(id => invoices.find(i => i.account_move_id === id)?.assignment_type === 'manual');
    if (manualIds.length === 0) {
      showToast(t('carrier.noManualAssignmentsSelected'), 'warn');
      setRemoveConfirm(null); setBulkAction(false); return;
    }
    setBulkAction(true);
    const res = await api('/api/carrier-kontrolle/remove-assignments', {
      method: 'POST', body: { account_move_ids: manualIds },
    });
    setBulkAction(false); setRemoveConfirm(null);
    if (res.ok) {
      showToast(`${res.deleted} ${t('carrier.assignmentsRemoved')}`);
      setSelected(new Set()); loadInvoices(); onChanged();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex flex-col m-4 bg-surface-elevated border border-line-hover rounded-card shadow-2xl overflow-hidden flex-1">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-line shrink-0">
          <div>
            <h2 className="text-lg font-bold">{t('carrier.manageInvoices')}</h2>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              {monthNames[monat - 1]} {jahr} — {t('carrier.assignIncomingToCarriers')}
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none">&times;</button>
        </div>

        {/* Filter bar */}
        <div className="p-4 border-b border-line shrink-0 flex items-center gap-3 flex-wrap">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('carrier.searchInvoiceShort')}
            className="bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-primary-500 w-72" />
          <select value={carrierFilter} onChange={e => { setCarrierFilter(e.target.value); setSelected(new Set()); }}
            className="bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none">
            <option value="">{t('carrier.allCarriers')}</option>
            <option value="__none__">{t('carrier.notAssigned')}</option>
            {carrierList.map(c => (
              <option key={c.carrier_key} value={c.carrier_key}>{c.display_name}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)] cursor-pointer select-none">
            <input type="checkbox" checked={manualOnly} onChange={e => { setManualOnly(e.target.checked); setSelected(new Set()); }}
              className="accent-primary-500" />
            {t('carrier.manualOnly')}
          </label>
          <span className="text-xs text-[var(--text-tertiary)] ml-auto">{invoices.length} {invoices.length !== 1 ? t('carrier.invoicesPlural') : t('carrier.invoiceSingular')}</span>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="px-4 py-3 border-b border-line bg-primary-500/5 shrink-0 flex items-center gap-3 flex-wrap">
            <span className="text-xs font-medium text-primary-400">{selected.size} {t('carrier.selected')}</span>
            <select value={bulkCarrier} onChange={e => setBulkCarrier(e.target.value)}
              className="bg-surface-inset border border-line-hover rounded px-2 py-1 text-xs text-content">
              <option value="">{t('carrier.selectCarrier')}</option>
              {carrierList.map(c => (
                <option key={c.carrier_key} value={c.carrier_key}>{c.display_name}</option>
              ))}
            </select>
            <button onClick={handleBulkReassign} disabled={!bulkCarrier || bulkAction}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2569A8] disabled:opacity-40 transition-colors">
              {bulkAction ? '...' : t('carrier.reassign')}
            </button>
            <button onClick={() => setRemoveConfirm('bulk')} disabled={bulkAction}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors">
              {t('carrier.removeAssignment')}
            </button>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center text-[var(--text-tertiary)] py-12">{t('carrier.loadingInvoicesFromOdoo')}</div>
          ) : invoices.length === 0 ? (
            <div className="text-center text-[var(--text-tertiary)] py-12">{t('carrier.noInvoicesFound')}</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface-elevated z-10">
                <tr className="border-b border-line text-content-tertiary">
                  <th className="text-center px-3 py-2 w-10">
                    <input type="checkbox" checked={selected.size === invoices.length && invoices.length > 0}
                      onChange={toggleSelectAll} className="accent-primary-500" />
                  </th>
                  <th className="text-left px-3 py-2">{t('carrier.invoiceNo')}</th>
                  <th className="text-left px-3 py-2">{t('carrier.vendor')}</th>
                  <th className="text-left px-3 py-2">{t('carrier.date')}</th>
                  <th className="text-right px-3 py-2">{t('carrier.amount')}</th>
                  <th className="text-left px-3 py-2">{t('carrier.assigned')}</th>
                  <th className="text-center px-3 py-2 w-52">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.account_move_id}
                    className={`border-b border-line/50 hover:bg-surface-hover transition-colors ${
                      selected.has(inv.account_move_id) ? 'bg-primary-500/5' : ''
                    } ${inv.assignment_type === 'manual' ? 'bg-amber-500/[0.03]' : ''}`}>
                    <td className="text-center px-3 py-2">
                      <input type="checkbox" checked={selected.has(inv.account_move_id)}
                        onChange={() => toggleSelect(inv.account_move_id)} className="accent-primary-500" />
                    </td>
                    <td className="px-3 py-2 font-mono text-content">{inv.name}</td>
                    <td className="px-3 py-2 text-content truncate max-w-[200px]">{inv.partner_name}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{inv.invoice_date}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-content">{fmtEur(inv.amount_untaxed)}</td>
                    <td className="px-3 py-2">
                      {inv.assigned_carrier_key ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: inv.assigned_carrier_color }} />
                          <span className="text-content">{inv.assigned_carrier_name}</span>
                          {inv.assignment_type === 'manual' && (
                            <Badge variant="info" className="text-[9px] px-1 py-0.5">{t('carrier.manual')}</Badge>
                          )}
                        </span>
                      ) : (
                        <span className="text-[var(--text-tertiary)] italic">{t('carrier.notAssigned')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {reassigning[inv.account_move_id] ? (
                          <span className="text-[var(--text-tertiary)] text-[10px]">...</span>
                        ) : (
                          <select defaultValue="" onChange={e => handleReassign(inv.account_move_id, e.target.value)}
                            className="bg-surface-inset border border-line-hover rounded px-1.5 py-1 text-[11px] text-content w-28">
                            <option value="" disabled>{t('carrier.change')}...</option>
                            {carrierList.filter(c => c.carrier_key !== inv.assigned_carrier_key).map(c => (
                              <option key={c.carrier_key} value={c.carrier_key}>{c.display_name}</option>
                            ))}
                          </select>
                        )}
                        {inv.assignment_type === 'manual' && (
                          removeConfirm === inv.account_move_id ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleRemoveOverride(inv.account_move_id)}
                                className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-600 text-white hover:bg-red-700">{t('common.yes')}</button>
                              <button onClick={() => setRemoveConfirm(null)}
                                className="px-1.5 py-0.5 rounded text-[10px] text-[var(--text-tertiary)] hover:text-content">{t('common.no')}</button>
                            </div>
                          ) : (
                            <button onClick={() => setRemoveConfirm(inv.account_move_id)}
                              className="text-[var(--text-tertiary)] hover:text-red-400 transition-colors shrink-0" title={t('carrier.removeManualAssignment')}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Bulk remove confirmation */}
      {removeConfirm === 'bulk' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRemoveConfirm(null)} />
          <div className="relative bg-surface-elevated border border-line-hover rounded-card p-5 w-96 shadow-2xl">
            <div className="text-sm text-content mb-4">
              {t('carrier.bulkRemoveConfirm', { count: [...selected].filter(id => invoices.find(i => i.account_move_id === id)?.assignment_type === 'manual').length })}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRemoveConfirm(null)}
                className="px-3 py-1.5 rounded-lg text-xs text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">
                {t('common.cancel')}
              </button>
              <button onClick={handleBulkRemove} disabled={bulkAction}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition-colors">
                {bulkAction ? '...' : t('carrier.remove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[70] px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-opacity ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' :
          toast.type === 'warn' ? 'bg-amber-600 text-white' :
          'bg-red-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════

export default function CarrierKontrolle() {
  const { t } = useLanguage();
  const monthNames = Array.from({length: 12}, (_, i) => t('common.months.' + i));
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [unassignedOpen, setUnassignedOpen] = useState(false);
  const [invoiceMgmtOpen, setInvoiceMgmtOpen] = useState(false);
  const [trendData, setTrendData] = useState([]);

  const loadData = useCallback(() => {
    setLoading(true);
    api(`/api/carrier-kontrolle/vergleich?jahr=${year}&monat=${month}`)
      .then(res => { if (res.carriers) setData(res); setLoading(false); })
      .catch(() => setLoading(false));
  }, [year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load 12-month trend for all carriers combined
  useEffect(() => {
    let vonY = year - 1, vonM = month + 1;
    if (vonM > 12) { vonM = 1; vonY++; }
    const von = `${vonY}-${String(vonM).padStart(2, '0')}`;
    const bis = `${year}-${String(month).padStart(2, '0')}`;
    api(`/api/carrier-kontrolle/trend?carrier=post&von=${von}&bis=${bis}`)
      .then(res => { if (res.months) setTrendData(res.months); });
  }, [year, month]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const carriers = data?.carriers || [];
  const totals = useMemo(() => {
    const t = { soll: 0, ist: 0, pakete: 0, adjSoll: 0, adjIst: 0, sollVk: 0, istVk: 0, margeSoll: 0, margeIst: 0 };
    carriers.forEach(c => {
      t.soll += c.soll_cost; t.ist += c.ist_cost;
      t.pakete += c.soll_pakete;
      t.adjSoll += c.adj_soll || 0; t.adjIst += c.adj_ist || 0;
      t.sollVk += c.soll_price || 0; t.istVk += c.ist_vk || 0;
      t.margeSoll += c.marge_soll || 0; t.margeIst += c.marge_ist || 0;
    });
    t.delta = t.ist - t.soll;
    t.deltaPct = t.soll > 0 ? (t.delta / t.soll) * 100 : 0;
    t.deltaVk = t.istVk - t.sollVk;
    t.eurPkt = t.pakete > 0 ? t.margeIst / t.pakete : 0;
    return t;
  }, [carriers]);

  const barData = useMemo(() => carriers.filter(c => c.soll_cost > 0 || c.ist_cost > 0 || c.soll_price > 0 || c.ist_vk > 0).map(c => ({
    name: c.display_name, 'SOLL EK': c.soll_cost, 'IST EK': c.ist_cost, 'SOLL VK': c.soll_price, 'IST VK': c.ist_vk || 0, color: c.color,
  })), [carriers]);

  const pieData = useMemo(() => carriers.filter(c => c.ist_cost > 0 || c.soll_cost > 0).map(c => ({
    name: c.display_name, value: c.ist_cost || c.soll_cost, color: c.color,
  })), [carriers]);

  const thCls = "text-right text-[10px] font-medium text-content-tertiary uppercase tracking-wider px-4 py-3";

  return (
    <div className="p-page space-y-section">
      {/* Header */}
      <PageHeader
        title={t('carrier.title')}
        subtitle={`${monthNames[month - 1]} ${year} — ${t('carrier.subtitle')}`}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={prevMonth}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
              </Button>
              <Button variant="ghost" size="sm" onClick={nextMonth}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </Button>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setInvoiceMgmtOpen(true)}
              className="bg-primary-500/10 border-primary-500/30 text-primary-400 hover:bg-primary-500/20">
              {t('carrier.manageInvoices')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setUnassignedOpen(true)}
              className="bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20">
              {t('carrier.notAssigned')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setMappingOpen(true)}>
              {t('carrier.carrierMapping')}
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label={t('carrier.sollEk')} value={loading ? '...' : fmtEur(totals.soll)} trend={loading ? '' : `${fmtNum(totals.pakete)} ${t('carrier.packages')}`} />
        <KpiCard label={t('carrier.istEk')} value={loading ? '...' : fmtEur(totals.ist)} trend={loading ? '' : t('carrier.incomingInvoices')} />
        <KpiCard label={t('carrier.deltaEk')} value={loading ? '...' : fmtEur(totals.delta)}
          trend={loading ? '' : fmtPct(totals.deltaPct)} trendUp={totals.delta <= 0} />
        <KpiCard label={t('carrier.istVk')} value={loading ? '...' : fmtEur(totals.istVk)} trend={loading ? '' : t('carrier.outgoingInvoices')} />
        <KpiCard label={t('carrier.marginIst')} value={loading ? '...' : fmtEur(totals.margeIst)}
          trend={loading ? '' : `${t('carrier.sollLabel')}: ${fmtEur(totals.margeSoll)}`} />
        <KpiCard label={t('carrier.avgEurPkt')} value={loading ? '...' : fmtEur(totals.eurPkt)}
          trend={loading ? '' : `${fmtNum(totals.pakete)} ${t('carrier.packages')}`} />
      </div>

      {/* Main Table */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className="text-left text-[10px] font-medium text-content-tertiary uppercase tracking-wider px-4 py-3">{t('carrier.carrier')}</th>
              <th className={thCls}>{t('carrier.packages')}</th>
              <th className={thCls}>{t('carrier.sollEk')}</th>
              <th className={thCls}>{t('carrier.istEk')}</th>
              <th className={thCls}>{t('carrier.deltaEk')}</th>
              <th className={thCls}>{t('carrier.sollVk')}</th>
              <th className={thCls}>{t('carrier.istVk')}</th>
              <th className={thCls}>{t('carrier.deltaVk')}</th>
              <th className={thCls}>{t('carrier.marginSoll')}</th>
              <th className={thCls}>{t('carrier.marginIst')}</th>
              <th className={thCls}>{t('carrier.eurPkt')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="text-center text-[var(--text-tertiary)] py-12">{t('carrier.loadingDataFromOdoo')}</td></tr>
            ) : carriers.length === 0 ? (
              <tr><td colSpan={11} className="text-center text-[var(--text-tertiary)] py-12">{t('common.noData')}</td></tr>
            ) : (
              <>
                {carriers.map(c => {
                  const adj = (c.adj_soll || 0) + (c.adj_ist || 0);
                  const ep = c.soll_pakete > 0 ? c.marge_ist / c.soll_pakete : 0;
                  return (
                  <tr key={c.carrier_key}
                    className={`border-b border-line/50 hover:bg-surface-hover transition-colors ${deltaBg(c.delta_pct)}`}>
                    <td className="px-4 py-3 relative" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                        <span className="font-medium text-content cursor-pointer" onClick={() => setDetail(c.carrier_key)}>{c.display_name}</span>
                        <AdjustmentCell
                          carrier={c.carrier_key} carrierName={c.display_name}
                          jahr={year} monat={month}
                          adjSoll={c.adj_soll} adjIst={c.adj_ist}
                          onSaved={loadData}
                          inline
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[var(--text-secondary)] cursor-pointer" onClick={() => setDetail(c.carrier_key)}>{fmtNum(c.soll_pakete)}</td>
                    <td className="px-4 py-3 text-right font-mono text-content cursor-pointer" onClick={() => setDetail(c.carrier_key)}>{fmtEur(c.soll_cost)}</td>
                    <td className="px-4 py-3 text-right font-mono text-content cursor-pointer" onClick={() => setDetail(c.carrier_key)}>{fmtEur(c.ist_cost)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold cursor-pointer text-[var(--text-primary)]" onClick={() => setDetail(c.carrier_key)}>
                      {c.delta > 0 ? '+' : ''}{fmtEur(c.delta)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-content cursor-pointer" onClick={() => setDetail(c.carrier_key)}>{fmtEur(c.soll_price)}</td>
                    <td className="px-4 py-3 text-right font-mono text-content cursor-pointer" onClick={() => setDetail(c.carrier_key)}>{fmtEur(c.ist_vk || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold cursor-pointer text-[var(--text-primary)]" onClick={() => setDetail(c.carrier_key)}>
                      {(c.delta_vk || 0) > 0 ? '+' : ''}{fmtEur(c.delta_vk || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-content cursor-pointer" onClick={() => setDetail(c.carrier_key)}>{fmtEur(c.marge_soll)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold cursor-pointer text-[var(--text-primary)]" onClick={() => setDetail(c.carrier_key)}>{fmtEur(c.marge_ist)}</td>
                    <td className={`px-4 py-3 text-right font-mono text-xs cursor-pointer ${c.soll_pakete > 0 ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`} onClick={() => setDetail(c.carrier_key)}>
                      {c.soll_pakete > 0 ? fmtEur(ep) : '—'}
                    </td>
                  </tr>
                  );
                })}
                {/* Totals row */}
                <tr className="bg-surface-inset/50 font-semibold">
                  <td className="px-4 py-3 text-content">{t('common.total').toUpperCase()}</td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--text-secondary)]">{fmtNum(totals.pakete)}</td>
                  <td className="px-4 py-3 text-right font-mono text-content">{fmtEur(totals.soll)}</td>
                  <td className="px-4 py-3 text-right font-mono text-content">{fmtEur(totals.ist)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--text-primary)]">
                    {totals.delta > 0 ? '+' : ''}{fmtEur(totals.delta)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-content">{fmtEur(totals.sollVk)}</td>
                  <td className="px-4 py-3 text-right font-mono text-content">{fmtEur(totals.istVk)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--text-primary)]">
                    {totals.deltaVk > 0 ? '+' : ''}{fmtEur(totals.deltaVk)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-content">{fmtEur(totals.margeSoll)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--text-primary)]">{fmtEur(totals.margeIst)}</td>
                  <td className={`px-4 py-3 text-right font-mono text-xs ${totals.pakete > 0 ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}>{totals.pakete > 0 ? fmtEur(totals.eurPkt) : '—'}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Charts */}
      {!loading && carriers.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="bg-surface-card border border-line rounded-card p-4 lg:col-span-2">
            <h3 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-3">{t('carrier.sollVsIstPerCarrier')}</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} barGap={2} barCategoryGap="20%" {...ANIMATION}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="name" {...AXIS_PROPS} />
                  <YAxis {...AXIS_PROPS} tickFormatter={fmtCompact} />
                  <Tooltip {...TOOLTIP_STYLE} formatter={(v) => fmtEur(v)} />
                  <Bar dataKey="SOLL EK" name={t('carrier.sollEk')} radius={[3, 3, 0, 0]} maxBarSize={22}>
                    {barData.map((entry, i) => <Cell key={i} fill={withOpacity(entry.color, 0.35)} />)}
                  </Bar>
                  <Bar dataKey="IST EK" name={t('carrier.istEk')} radius={[3, 3, 0, 0]} maxBarSize={22}>
                    {barData.map((entry, i) => <Cell key={i} fill={withOpacity(entry.color, 0.6)} />)}
                  </Bar>
                  <Bar dataKey="SOLL VK" name={t('carrier.sollVk')} radius={[3, 3, 0, 0]} maxBarSize={22}>
                    {barData.map((entry, i) => <Cell key={i} fill={withOpacity(entry.color, 0.8)} />)}
                  </Bar>
                  <Bar dataKey="IST VK" name={t('carrier.istVk')} radius={[3, 3, 0, 0]} maxBarSize={22}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                  <Legend {...LEGEND_STYLE} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-surface-card border border-line rounded-card p-4">
            <h3 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-3">{t('carrier.costDistribution')}</h3>
            <div className="h-64 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={65} outerRadius={88} paddingAngle={2} dataKey="value"
                    label={({ cx, cy, midAngle, outerRadius: or, percent }) => {
                      if (percent < 0.05) return null;
                      const r = or + 16;
                      const x = cx + r * Math.cos(-midAngle * Math.PI / 180);
                      const y = cy + r * Math.sin(-midAngle * Math.PI / 180);
                      return <text x={x} y={y} fill="var(--text-muted, #94a3b8)" fontSize={10} fontFamily="inherit" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">{(percent * 100).toFixed(0)}%</text>;
                    }}
                    labelLine={false}
                    {...ANIMATION}
                  >
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} stroke="none" />)}
                  </Pie>
                  <Tooltip {...TOOLTIP_STYLE} formatter={(v) => fmtEur(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <div className="text-lg font-bold text-content">{fmtCompact(pieData.reduce((s, d) => s + d.value, 0))}</div>
                  <div className="text-[10px] text-[var(--text-tertiary)]">{t('common.total')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trend Line */}
      {!loading && trendData.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card p-4">
          <h3 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-3">{t('carrier.postMonthlyTrend')}</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} {...ANIMATION}>
                <defs>
                  <linearGradient id="mainIstGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={MOD} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={MOD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="monat" {...AXIS_PROPS} tickFormatter={v => { const [,mm] = v.split('-'); return monthNames[parseInt(mm)-1]?.substring(0,3); }} />
                <YAxis {...AXIS_PROPS} tickFormatter={fmtCompact} />
                <Tooltip {...TOOLTIP_STYLE}
                  formatter={(v) => fmtEur(v)} labelFormatter={v => { const [yy,mm] = v.split('-'); return monthNames[parseInt(mm)-1] + ' ' + yy; }} />
                <Area type="monotone" dataKey="ist" name={t('carrier.istRe')} stroke={MOD} strokeWidth={2.5} fill="url(#mainIstGrad)" dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="soll" name={t('carrier.sollEk')} stroke={withOpacity('#f59e0b', 0.7)} strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                <Legend {...LEGEND_STYLE} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Modals */}
      {detail && <DetailModal carrier={detail} jahr={year} monat={month} onClose={() => setDetail(null)} />}
      {mappingOpen && <MappingModal onClose={() => setMappingOpen(false)} onSaved={loadData} />}
      {unassignedOpen && <UnassignedModal jahr={year} monat={month} onClose={() => setUnassignedOpen(false)} onAssigned={loadData} />}
      {invoiceMgmtOpen && <InvoiceManagementModal jahr={year} monat={month} onClose={() => setInvoiceMgmtOpen(false)} onChanged={loadData} />}
    </div>
  );
}
